import React, { ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import Layout from 'antd/lib/layout'
import isNil from 'lodash/isNil'
import { useSelector } from 'react-redux'
import store, { RootState } from '@app-store/store'
import ControllerFooter from '@components/ControllerFooter'
import routes from '@utils/constants/routes'
import { history } from '@components/App'
import { rootActions } from '@app-store/slices'
import { ISelectCompaniesByTokenPayload } from '@app-store/slices/sharedLists/selectCompaniesByToken'
import { IAddToAgendaByTokenPayload } from '@store/slices/meetings/addToAgendaByToken'
import { IMatchesByTokenDataItems } from '@app-store/slices/ui/matchesByToken.slice'
import { STATUS } from '@features/General'
import Logo from '@components/Layouts/Logo'
import styles from './index.scss'

dayjs.extend(utc)

interface IMatchesByTokenLayout {
    title: string
    children: ReactNode
    subTitle?: ReactNode
    widthPage?: string
}
export type LocationStateType = {
    fromSuggestedTime?: boolean
    fromSuggestedTimeByToken?: boolean
    from?: string
    fromEditItp?: boolean
}
function MatchesByTokenLayout(props: IMatchesByTokenLayout) {
    // Can check the location from and return user to that router after submiting data
    const { children, title, widthPage = '100%', subTitle = null } = props
    const state = useSelector((state: RootState) => state)
    const params: any = useParams()
    return (
        <Layout className={`${styles['step-layout']} ${styles['select-match-list-layout']}`}>
            <Logo />
            <Layout.Content className={styles['step-layout__content']} style={{ width: widthPage }}>
                <div className={styles['step-layout__title']}>
                    <h1>{title}</h1>
                    <div>{!isNil(subTitle) ? subTitle : null}</div>
                </div>
                {children}
            </Layout.Content>
            <ControllerFooter
                keyRoute={routes.matching.match_list_select._.replace(':token', params.token)}
                steps={makeSteps(state, params).filter(item => item.hide !== true)}
                hasLeave={false}
            />
        </Layout>
    )
}

export const onSubmitCalendar = (data, token, isMeetingHubEvent, availabilityData = null, oldUnavailables = null) => {
    const UIMatchesByTokenData: IMatchesByTokenDataItems = {
        selectedCompanies: data.selectedCompanies,
        rejectedCompanies: data.rejectedCompanies
    }
    const selectedSlots = data.selectedSlots.map(item => dayjs(item, 'YYYY-MM-DD hh:mm AM').utc())
    const deletedSlots = oldUnavailables
        ?.filter(
            item => !selectedSlots.find(x => dayjs(item.startDate, 'YYYY-MM-DD hh:mm AM').utc().isSame(x, 'second'))
        )
        ?.map(item => item._id)
    const unavailabilities = oldUnavailables?.map(item => dayjs(item.startDate, 'YYYY-MM-DD hh:mm AM').utc())
    const newSelectedSlots = selectedSlots
        .filter(item => !unavailabilities.find(x => item.isSame(x, 'second')))
        .map(item => item.format())
    const payload: ISelectCompaniesByTokenPayload = {
        data: UIMatchesByTokenData,
        token,
        sagaCB: {
            onSuccess: () => {
                store.dispatch(
                    rootActions.ui.matchesByToken.addItems({
                        selectedCompanies: [],
                        rejectedCompanies: []
                    })
                )
                if (!isMeetingHubEvent) {
                    if (deletedSlots?.length) {
                        const deletePayload = {
                            token,
                            ids: deletedSlots,
                            sagaCB: {
                                onSuccess: () => {
                                    if (!newSelectedSlots.length) {
                                        history.push(routes.matching.match_list_select.successful_calendar._)
                                    }
                                }
                            }
                        }
                        store.dispatch(rootActions.meetings.deleteUnavailabilityByToken.onRequest(deletePayload))
                    }
                    if (newSelectedSlots?.length) {
                        const UISyncCalendarByTokenData = {
                            timeslots: newSelectedSlots.map(item => ({
                                startDate: dayjs(item, 'YYYY-MM-DD hh:mm AM').utc().format(),
                                endDate: dayjs(item, 'YYYY-MM-DD hh:mm AM').add(15, 'm').utc().format()
                            }))
                        }
                        const payload: IAddToAgendaByTokenPayload = {
                            data: UISyncCalendarByTokenData,
                            token,
                            sagaCB: {
                                onSuccess: () => {
                                    store.dispatch(
                                        rootActions.ui.matchesByToken.addItems({
                                            selectedSlots: []
                                        })
                                    )
                                    history.push(routes.matching.match_list_select.successful_calendar._)
                                }
                            }
                        }
                        store.dispatch(rootActions.meetings.addToAgendaByToken.onRequest(payload))
                    } else if (!deletedSlots?.length) {
                        history.push(routes.matching.match_list_select.successful_calendar._)
                    }
                } else {
                    const action = availabilityData.id ? 'editAvailabilityByToken' : 'createAvailabilityByToken'
                    store.dispatch(rootActions.calendar[action].onRequest({ ...availabilityData, token }))
                }
            }
        }
    }
    store.dispatch(rootActions.sharedLists.selectCompaniesByToken.onRequest(payload))
}

const makeSteps = (state: RootState, params) => {
    const { data } = { ...state.ui.matchesByToken }
    const { data: listByToken } = { ...state.sharedLists.infoByToken }
    const { status: editAvailabilityStatus } = useSelector((state: RootState) => state.calendar.editAvailabilityByToken)
    const { status: createAvailabilityStatus } = useSelector(
        (state: RootState) => state.calendar.createAvailabilityByToken
    )
    const { status: selectCompaniesStatus } = useSelector(
        (state: RootState) => state.sharedLists.selectCompaniesByToken
    )
    const { status: addToAgendaByTokenStatus } = useSelector((state: RootState) => state.meetings.addToAgendaByToken)
    const { data: unavailableSlotsData } = useSelector((state: RootState) => state.meetings.getUnavailableSlotsByToken)
    const isSubmitting = [
        editAvailabilityStatus,
        createAvailabilityStatus,
        selectCompaniesStatus,
        addToAgendaByTokenStatus
    ].includes(STATUS.RUNNING)
    const isMeetingHub = unavailableSlotsData?.isMeetingHubEvent || false
    // ON Select companies
    const onSelectCompanies = () => {
        history.push(routes.matching.match_list_select.sync_calendar._.replace(':token', params.token))
    }

    const config: any = [
        {
            key: 'match_list',
            errorText:
                listByToken?.minimumSelectionNo && data.selectedCompanies?.length < listByToken?.minimumSelectionNo
                    ? `To optimize the scheduling of your preferred meetings, please select at
            least ${listByToken.minimumSelectionNo} matches. This ensures a wider market reach and
            improved meeting opportunities for you.`
                    : '',
            routes: [
                {
                    route: 'match_list',
                    skip: false,
                    back: false,
                    next: {
                        onClick: () => onSelectCompanies(),
                        disabled: data.selectedCompanies?.length < (listByToken?.minimumSelectionNo || 1),
                        text: 'Continue'
                    }
                }
            ],
            stepText: 'Match list'
        },
        {
            key: 'sync_calendar',
            routes: [
                {
                    route: 'sync_calendar',
                    skip: isMeetingHub,
                    back: true,
                    next: {
                        onClick: isMeetingHub
                            ? () =>
                                  history.push(
                                      routes.matching.match_list_select.availability_calendar._.replace(
                                          ':token',
                                          params.token
                                      )
                                  )
                            : () =>
                                  onSubmitCalendar(
                                      data,
                                      params.token,
                                      isMeetingHub,
                                      null,
                                      unavailableSlotsData?.unavailable?.filter(item => item.type === 'User')
                                  ),
                        isFetching: isSubmitting,
                        disabled: isSubmitting,
                        text: isMeetingHub ? 'Continue' : 'Submit'
                    }
                }
            ],
            stepText: 'Sync Calendar'
        },
        {
            key: 'availability_calendar',
            routes: [
                {
                    route: 'availability_calendar',
                    skip: false,
                    back: true,
                    nextIsSubmit: true,
                    next: {
                        isFetching: isSubmitting,
                        disabled: isSubmitting,
                        text: 'Submit'
                    }
                }
            ],
            hide: !isMeetingHub,
            stepText: 'Availability Calendar'
        }
    ]

    return config
}
export default React.memo(MatchesByTokenLayout)
